<!-- Filter: a bottom sheet of chips. Filters DIM the wall rather than empty it,
     so the footer says live how many coffees stay lit. Sage chips, ink when on;
     the one ink button shows the count. -->
<template>
  <IonModal
    :is-open="open"
    :breakpoints="[0, 0.82, 1]"
    :initial-breakpoint="0.82"
    :handle="true"
    :expand-to-scroll="false"
    class="filter-sheet"
    aria-label="Filter"
    @did-dismiss="$emit('close')"
  >
    <IonHeader class="ion-no-border">
      <IonToolbar>
        <IonTitle>Filter</IonTitle>
        <IonButtons slot="end">
          <IonButton :disabled="!activeFilterCount" @click="clear">Clear filters</IonButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>

    <IonContent class="ion-padding filter-sheet__content">
      <section class="group" aria-labelledby="f-flavour">
        <h3 id="f-flavour" class="group__title">Flavour</h3>
        <div class="chips">
          <IonChip
            v-for="f in FLAVOURS"
            :key="f.id"
            :outline="!has('flavours', f.id)"
            :class="{ on: has('flavours', f.id) }"
            role="checkbox"
            :aria-checked="has('flavours', f.id)"
            @click="toggle('flavours', f.id)"
          >
            <IonIcon :icon="FLAVOUR_ICONS[f.id]" aria-hidden="true" :style="{ color: has('flavours', f.id) ? undefined : f.colour }" />
            <IonLabel>{{ f.short }}</IonLabel>
          </IonChip>
        </div>
      </section>

      <section class="group" aria-labelledby="f-brew">
        <h3 id="f-brew" class="group__title">Brew style</h3>
        <div class="chips">
          <IonChip
            v-for="b in BREWS"
            :key="b"
            :outline="!has('brews', b)"
            :class="{ on: has('brews', b) }"
            role="checkbox"
            :aria-checked="has('brews', b)"
            @click="toggle('brews', b)"
          >
            <IonLabel>{{ b }}</IonLabel>
          </IonChip>
        </div>
      </section>

      <section class="group" aria-labelledby="f-kind">
        <h3 id="f-kind" class="group__title">Type</h3>
        <div class="chips">
          <IonChip
            v-for="k in KINDS"
            :key="k"
            :outline="!has('kinds', k)"
            :class="{ on: has('kinds', k) }"
            role="checkbox"
            :aria-checked="has('kinds', k)"
            @click="toggle('kinds', k)"
          >
            <IonLabel>{{ k }}</IonLabel>
          </IonChip>
        </div>
      </section>

      <section class="group" aria-labelledby="f-origin">
        <h3 id="f-origin" class="group__title">Origin</h3>
        <div class="chips">
          <IonChip
            v-for="o in shownOrigins"
            :key="o.name"
            :outline="!has('origins', o.name)"
            :class="{ on: has('origins', o.name) }"
            role="checkbox"
            :aria-checked="has('origins', o.name)"
            @click="toggle('origins', o.name)"
          >
            <IonLabel>{{ o.name }} <span class="count">{{ o.count }}</span></IonLabel>
          </IonChip>
          <IonChip v-if="allOrigins.length > ORIGIN_TOP" class="more" @click="moreOrigins = !moreOrigins">
            <IonLabel>{{ moreOrigins ? 'Fewer origins' : `${allOrigins.length - ORIGIN_TOP} more origins` }}</IonLabel>
          </IonChip>
        </div>
      </section>

      <section class="group" aria-labelledby="f-more">
        <h3 id="f-more" class="group__title">Stock and price</h3>
        <IonList lines="none" class="rows">
          <IonItem>
            <IonToggle v-model="filters.inStockOnly">In stock only</IonToggle>
          </IonItem>
          <IonItem>
            <div class="price">
              <div class="price__head">
                <span id="f-price">Max price</span>
                <strong>{{ filters.maxPrice == null ? 'Any' : `$${filters.maxPrice}` }}</strong>
              </div>
              <IonRange
                aria-labelledby="f-price"
                :min="priceMin"
                :max="priceMax"
                :step="1"
                :value="filters.maxPrice ?? priceMax"
                :pin="true"
                :pin-formatter="(v) => `$${v}`"
                @ion-input="setPrice($event.detail.value)"
              />
            </div>
          </IonItem>
        </IonList>
      </section>
    </IonContent>

    <IonFooter class="ion-no-border filter-sheet__footer">
      <div class="footer">
        <IonButton expand="block" class="show" @click="$emit('close')">
          {{ showLabel }}
        </IonButton>
      </div>
    </IonFooter>
  </IonModal>
</template>

<script setup>
import { computed, ref } from 'vue';
import {
  IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent, IonChip, IonLabel,
  IonIcon, IonList, IonItem, IonToggle, IonRange, IonFooter,
} from '@ionic/vue';
import {
  nutritionOutline, flaskOutline, leafOutline, sunnyOutline, sparklesOutline, flowerOutline,
} from 'ionicons/icons';
import {
  FLAVOURS, BREWS, KINDS, allOrigins, filters, clearFilters, activeFilterCount, matchingSlugs, coffees,
} from '../store.js';
import { haptic } from '../core/util.js';

defineProps({ open: { type: Boolean, default: false } });
defineEmits(['close']);

const FLAVOUR_ICONS = {
  chocolate: nutritionOutline, caramel: flaskOutline, berry: leafOutline,
  citrus: sunnyOutline, tropical: sparklesOutline, floral: flowerOutline,
};

const has = (k, v) => filters[k].includes(v);

// The six biggest origins, plus any already chosen; the long tail on request.
const ORIGIN_TOP = 6;
const moreOrigins = ref(false);
const shownOrigins = computed(() => (moreOrigins.value
  ? allOrigins.value
  : allOrigins.value.filter((o, i) => i < ORIGIN_TOP || filters.origins.includes(o.name))));
function toggle (k, v) {
  haptic('tick');
  const i = filters[k].indexOf(v);
  if (i >= 0) filters[k].splice(i, 1); else filters[k].push(v);
}
function clear () { haptic('tick'); clearFilters(); }

const prices = computed(() => coffees.value.map((c) => c.fromPrice).filter((p) => p != null));
const priceMin = computed(() => Math.floor(Math.min(...prices.value)));
const priceMax = computed(() => Math.ceil(Math.max(...prices.value)));
function setPrice (v) { filters.maxPrice = v >= priceMax.value ? null : v; }

const showLabel = computed(() => {
  const n = matchingSlugs.value.size;
  if (!n) return 'No coffees match';
  return `Show ${n} ${n === 1 ? 'coffee' : 'coffees'}`;
});
</script>

<style scoped>
.filter-sheet {
  --background: var(--dock, #FAF7F1);
  --border-radius: var(--radius-sheet, 24px);
}
.filter-sheet ion-toolbar { --background: var(--dock, #FAF7F1); --color: var(--ink, #1F2A22); }
.filter-sheet ion-title {
  font-family: var(--font-display, 'Bricolage Grotesque', sans-serif);
  font-weight: 800;
  font-size: var(--text-xl);
  letter-spacing: -0.02em;
}
.filter-sheet ion-toolbar ion-button { --color: var(--ink-2, #3E4A41); font-weight: 700; text-transform: none; }
.filter-sheet__content { --background: var(--dock, #FAF7F1); --padding-bottom: 24px; }
.group + .group { margin-top: 22px; }
.group__title {
  margin: 0 0 8px;
  font-family: var(--font-mono, 'Martian Mono', monospace);
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted, #5E6B60);
}
.chips { display: flex; flex-wrap: wrap; gap: 2px; margin: 0 -4px; }
ion-chip {
  --background: var(--sage, #E6EDE4);
  --color: var(--ink-2, #3E4A41);
  min-height: 40px;
  padding-inline: 14px;
  border: 0;
  border-radius: var(--radius-pill, 999px);
  font-family: var(--font-body, 'Manrope', sans-serif);
  font-size: var(--text-sm);
  font-weight: 700;
  margin: 4px;
}
ion-chip.chip-outline { border: 0; }
ion-chip.on {
  --background: var(--ink, #1F2A22);
  --color: var(--dock, #FAF7F1);
}
ion-chip.more { --background: transparent; --color: var(--muted, #5E6B60); text-decoration: underline; text-underline-offset: 0.2em; }
ion-chip ion-icon { font-size: var(--text-lg); margin-inline-end: 6px; }
ion-chip.on ion-icon { color: var(--mustard, #F2C230) !important; }
.count { font-family: var(--font-mono, 'Martian Mono', monospace); font-weight: 500; font-size: 11px; opacity: 0.65; margin-inline-start: 6px; font-variant-numeric: tabular-nums; }
.rows { background: transparent; padding: 0; }
.rows ion-item { --padding-start: 0; --inner-padding-end: 0; --background: transparent; --color: var(--ink, #1F2A22); font-weight: 600; }
.rows ion-toggle { --track-background-checked: var(--leaf, #3F7A4E); }
.rows ion-range { --bar-background-active: var(--ink, #1F2A22); --knob-background: var(--ink, #1F2A22); --pin-background: var(--ink, #1F2A22); --pin-color: var(--dock, #FAF7F1); }
.price { width: 100%; padding: 8px 0; }
.price__head { display: flex; justify-content: space-between; font-weight: 600; color: var(--ink, #1F2A22); }
.price__head strong { font-family: var(--font-mono, 'Martian Mono', monospace); font-weight: 500; font-variant-numeric: tabular-nums; }
.footer { padding: 12px 16px calc(12px + env(safe-area-inset-bottom)); background: var(--dock, #FAF7F1); border-top: 1px solid var(--line, #ECE6DC); }
.show {
  --background: var(--ink, #1F2A22);
  --background-activated: var(--ink-2, #3E4A41);
  --color: var(--dock, #FAF7F1);
  --border-radius: var(--radius-pill, 999px);
  --box-shadow: none;
  min-height: var(--btn-height, 52px);
  font-family: var(--font-body, 'Manrope', sans-serif);
  font-size: var(--text-md);
  font-weight: 800;
  text-transform: none;
  letter-spacing: 0;
  margin: 0;
}
</style>
